import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  Plus,
  Edit2,
  Trash2,
  Play,
  Pause,
  Phone,
  Building2,
  Clock,
  Calendar,
  AlertCircle,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';
import api, { TokenStorage } from '../../utils/api';
import { API_BASE } from '../../utils/config';
import AppShell from '../layout/AppShell';
import CreateNotificationModal from './CreateNotificationModal';
import EditNotificationModal from './EditNotificationModal';
import { SkeletonCard } from '../common/SkeletonLoader';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function NotificationsPanel() {
  const navigate = useNavigate();
  const useCookieFlow = (API_BASE || '').startsWith('http://localhost');
  const [csrfToken] = useState(localStorage.getItem('csrfToken'));
  const [notifications, setNotifications] = useState([]);
  const [phones, setPhones] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Plan limits
  const [maxAllowed, setMaxAllowed] = useState(null);
  const [planLabel, setPlanLabel] = useState('');
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const handleLogout = async () => {
    try {
      if (useCookieFlow) {
        await api.post('/api/auth/logout', {}, { headers: { 'X-CSRF-Token': csrfToken }, withCredentials: true });
      }
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      TokenStorage.removeToken();
      navigate('/logged-out', { replace: true });
    }
  };

  async function loadData() {
    try {
      setLoading(true);
      
      const [notifRes, phonesRes, accountsRes, subRes, plansRes] = await Promise.allSettled([
        api.get('/api/notifications?includeInactive=true'),
        api.get('/api/sms/phones'),
        api.get('/api/plaid/accounts'),
        api.get('/api/subscriptions/current'),
        api.get('/api/subscriptions/plans')
      ]);

      // Notifications
      if (notifRes.status === 'fulfilled') {
        const data = notifRes.value.data;
        setNotifications(Array.isArray(data) ? data : (data?.items || []));
      }

      // Phones
      if (phonesRes.status === 'fulfilled') {
        setPhones(phonesRes.value.data?.phones || []);
      }

      // Accounts
      if (accountsRes.status === 'fulfilled') {
        const data = accountsRes.value.data;
        setAccounts(data?.accounts || data?.items || []);
      }

      // Plan limits
      let priceId = null;
      if (subRes.status === 'fulfilled') {
        priceId = subRes.value?.data?.priceId || subRes.value?.data?.price_id;
      }
      
      if (plansRes.status === 'fulfilled') {
        const plans = plansRes.value?.data?.plans || [];
        const max = deriveMaxFromPlans(plans, priceId);
        setMaxAllowed(max);
        const label = (plans.find(pl => pl.id === priceId)?.nickname) || '';
        setPlanLabel(label);
      }

      setError('');
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }

  function deriveMaxFromPlans(plans, priceId) {
    if (!priceId) return 1;
    const p = (plans || []).find(pl => pl.id === priceId);
    if (!p) return 1;

    const meta = p.metadata || {};
    if (meta.notifications_per_day) {
      const n = parseInt(meta.notifications_per_day, 10);
      if (!Number.isNaN(n) && n > 0) return n;
    }

    if (Array.isArray(p.features)) {
      const hit = p.features.find(f => /\b(1|2|3)\s*SMS\s*per\s*day\b/i.test(f));
      if (hit) {
        const m = hit.match(/\b(1|2|3)\b/);
        if (m) return parseInt(m[1], 10);
      }
    }

    if (typeof p.nickname === 'string') {
      if (/three|3/i.test(p.nickname)) return 3;
      if (/two|2/i.test(p.nickname)) return 2;
      if (/one|1/i.test(p.nickname)) return 1;
    }

    return 1;
  }

  async function handleToggleStatus(notification) {
    // Check if trying to activate and would exceed limit
    if (!notification.is_active) {
      const activeCount = notifications.filter(n => n.is_active).length;
      if (typeof maxAllowed === 'number' && activeCount >= maxAllowed) {
        alert(`You've reached your limit of ${maxAllowed} active notifications. Please upgrade your plan or pause another notification first.`);
        return;
      }
    }

    try {
      await api.patch(`/api/notifications/${notification.id}/status`);
      await loadData();
    } catch (err) {
      console.error('Error toggling notification status:', err);
      alert('Failed to update notification status');
    }
  }

  async function handleDelete(notification) {
    if (!window.confirm(`Delete notification "${notification.title}"?`)) {
      return;
    }

    try {
      await api.delete(`/api/notifications/${notification.id}`);
      await loadData();
    } catch (err) {
      console.error('Error deleting notification:', err);
      alert('Failed to delete notification');
    }
  }

  function handleEdit(notification) {
    setSelectedNotification(notification);
    setShowEditModal(true);
  }

  function handleCreateSuccess() {
    setShowCreateModal(false);
    loadData();
  }

  function handleEditSuccess() {
    setShowEditModal(false);
    setSelectedNotification(null);
    loadData();
  }

  function handleModalClose() {
    setShowCreateModal(false);
    setShowEditModal(false);
    setSelectedNotification(null);
  }

  function getStatusBadge(notification) {
    if (!notification.is_active) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
          <Pause className="w-3 h-3" />
          Paused
        </span>
      );
    }

    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
        <CheckCircle className="w-3 h-3" />
        Active
      </span>
    );
  }

  function formatSchedule(n) {
    const freq = n.frequency || 'daily';
    let timeStr = '';
    
    if (n.next_run_at) {
      const tz = n.tz_id || Intl.DateTimeFormat().resolvedOptions().timeZone;
      try {
        const dt = new Date(n.next_run_at);
        timeStr = new Intl.DateTimeFormat(undefined, {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
          timeZone: tz
        }).format(dt);
      } catch (e) {
        timeStr = '';
      }
    }

    if (freq === 'weekly') {
      const rawDays = n.weekdays || n.byweekday || n.by_weekday;
      const days = normalizeAndConvertWeekdays(rawDays);
      const dayStr = days.length > 0 ? days.map(idx => DAY_NAMES[idx]).join(', ') : 'all days';
      return `Weekly on ${dayStr}${timeStr ? ` at ${timeStr}` : ''}`;
    }

    if (freq === 'monthly') {
      const rawDays = n.monthdays || n.bymonthday || n.by_monthday;
      const days = normalizeMonthdays(rawDays);
      const dayStr = days.length > 0 ? days.map(d => ordinal(d)).join(', ') : 'the 1st';
      return `Monthly on ${dayStr}${timeStr ? ` at ${timeStr}` : ''}`;
    }

    return `Daily${timeStr ? ` at ${timeStr}` : ''}`;
  }

  function normalizeAndConvertWeekdays(raw) {
    let normalized = [];
    if (Array.isArray(raw)) {
      normalized = raw.map(v => typeof v === 'number' ? v : Number(v)).filter(v => Number.isFinite(v));
    } else if (typeof raw === 'string') {
      const inner = raw.startsWith('{') && raw.endsWith('}') ? raw.slice(1, -1) : raw;
      if (inner.trim()) {
        normalized = inner.split(',').map(s => Number(s.trim())).filter(v => Number.isFinite(v));
      }
    }
    
    // Convert from database format (1-7, Mon-Sun) to UI format (0-6, Sun-Sat)
    return normalized.map(day => day === 7 ? 0 : day);
  }

  function normalizeMonthdays(raw) {
    if (Array.isArray(raw)) {
      return raw.map(v => typeof v === 'number' ? v : Number(v)).filter(v => Number.isFinite(v));
    }
    if (typeof raw === 'string') {
      const inner = raw.startsWith('{') && raw.endsWith('}') ? raw.slice(1, -1) : raw;
      if (inner.trim()) {
        return inner.split(',').map(s => Number(s.trim())).filter(v => Number.isFinite(v));
      }
    }
    return [];
  }

  function ordinal(n) {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  }

  function formatNextRun(notification) {
    if (!notification.next_run_at) return '—';
    
    const tz = notification.tz_id || Intl.DateTimeFormat().resolvedOptions().timeZone;
    try {
      const dt = new Date(notification.next_run_at);
      return new Intl.DateTimeFormat(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: tz
      }).format(dt);
    } catch (e) {
      return '—';
    }
  }

  const activeCount = notifications.filter(n => n.is_active).length;
  const atLimit = typeof maxAllowed === 'number' && activeCount >= maxAllowed;

  if (loading) {
    return (
      <AppShell onLogout={handleLogout}>
        <div className="space-y-4 sm:space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold font-display text-gray-900">Notifications</h2>
              <p className="text-sm sm:text-base text-gray-600 mt-1">Manage your SMS balance notifications</p>
            </div>
            <div className="w-full sm:w-40 h-10 bg-gray-200 rounded-lg animate-pulse"></div>
          </div>
          <div className="h-16 bg-gray-100 rounded-lg animate-pulse"></div>
          <div className="grid gap-4">
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </div>
        </div>
      </AppShell>
    );
  }

  if (error) {
    return (
      <AppShell onLogout={handleLogout}>
        <div className="rounded-lg bg-red-50 p-4 border border-red-200">
          <div className="flex items-center gap-2 text-red-700">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell onLogout={handleLogout}>
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold font-display text-gray-900">Notifications</h2>
            <p className="text-sm sm:text-base text-gray-600 mt-1">Manage your SMS balance notifications</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            disabled={atLimit}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm sm:text-base rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors shadow-sm w-full sm:w-auto"
          >
            <Plus className="w-4 h-4" />
            <span className="whitespace-nowrap">New Notification</span>
          </button>
        </div>

        {/* Plan Limit Info */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex items-center gap-3">
            <Bell className="w-5 h-5 text-gray-600 flex-shrink-0" />
            <div>
              <div className="font-medium text-gray-900">
                {activeCount} of {maxAllowed || '∞'} active notifications
              </div>
              {planLabel && (
                <div className="text-sm text-gray-600">{planLabel} plan</div>
              )}
              {notifications.length > activeCount && (
                <div className="text-sm text-gray-500">
                  {notifications.length - activeCount} paused
                </div>
              )}
            </div>
          </div>
          {atLimit && (
            <a
              href="/dashboard/billing"
              className="inline-flex items-center min-h-[44px] sm:min-h-0 text-sm font-medium text-blue-600 hover:text-blue-700"
            >
              Upgrade plan →
            </a>
          )}
        </div>

        {/* At Limit Warning */}
        {atLimit && (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-medium text-amber-900">
                  You've reached your notification limit
                </div>
                <div className="text-sm text-amber-800 mt-1">
                  Upgrade your plan to create more notifications.{' '}
                  <a href="/dashboard/billing" className="underline hover:text-amber-900">
                    View plans
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Empty State */}
        {notifications.length === 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sm:p-12">
            <div className="text-center max-w-md mx-auto">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 mb-4">
                <Bell className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Create your first notification
              </h3>
              <p className="text-gray-600 mb-6">
                Set up SMS alerts to stay informed about your bank account balances.
              </p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
              >
                <Plus className="w-5 h-5" />
                New Notification
              </button>
            </div>
          </div>
        )}

        {/* Notifications Grid */}
        {notifications.length > 0 && (
          <div className="grid gap-3 sm:gap-4">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4 sm:mb-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-2">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900 break-words">
                        {notification.title || 'Untitled Notification'}
                      </h3>
                      {getStatusBadge(notification)}
                    </div>
                    
                    <div className="flex flex-wrap gap-3 sm:gap-4 text-xs sm:text-sm text-gray-600">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        <span>{formatSchedule(notification)}</span>
                      </div>
                      
                      {notification.phone_name && (
                        <div className="flex items-center gap-1">
                          <Phone className="w-4 h-4" />
                          <span>{notification.phone_name}</span>
                        </div>
                      )}
                      
                      {notification.bank_account_name && (
                        <div className="flex items-center gap-1">
                          <Building2 className="w-4 h-4" />
                          <span>
                            {notification.bank_account_name}
                            {notification.bank_account_mask && ` (****${notification.bank_account_mask})`}
                          </span>
                        </div>
                      )}
                    </div>

                    {notification.next_run_at && (
                      <div className="mt-3 text-sm text-gray-500">
                        <Clock className="inline w-3.5 h-3.5 mr-1" />
                        Next: <strong>{formatNextRun(notification)}</strong>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-1 sm:ml-4 justify-end sm:justify-start">
                    <button
                      onClick={() => handleToggleStatus(notification)}
                      className="p-1.5 sm:p-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title={notification.is_active ? 'Pause' : 'Resume'}
                      aria-label={notification.is_active ? 'Pause' : 'Resume'}
                    >
                      {notification.is_active ? (
                        <Pause className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      ) : (
                        <Play className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      )}
                    </button>
                    <button
                      onClick={() => handleEdit(notification)}
                      className="p-1.5 sm:p-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Edit"
                      aria-label="Edit notification"
                    >
                      <Edit2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(notification)}
                      className="p-1.5 sm:p-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete"
                      aria-label="Delete notification"
                    >
                      <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Modals */}
        {showCreateModal && (
          <CreateNotificationModal
            onClose={handleModalClose}
            onSuccess={handleCreateSuccess}
            phones={phones}
            accounts={accounts}
          />
        )}

        {showEditModal && selectedNotification && (
          <EditNotificationModal
            notification={selectedNotification}
            onClose={handleModalClose}
            onSuccess={handleEditSuccess}
            phones={phones}
            accounts={accounts}
          />
        )}
      </div>
    </AppShell>
  );
}

export default NotificationsPanel;
